import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { PasswordCaptureFields } from '../../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SliderNumericField } from '@/components/ui/numeric-input';
import { CompactColorPicker } from '@/components/ui/compact-color-picker';
import { Switch } from '@/components/ui/switch';
import { Eye } from 'lucide-react';

interface StyleTabProps {
  form: UseFormReturn<PasswordCaptureFields>;
}

export function StyleTab({ form }: StyleTabProps) {
  useLanguage();
  const style = form.watch('style');
  const overlayEnabled = form.watch('layout.overlay_opacity') > 0;

  return (
    <div className="space-y-6">
      {/* Cores e Estilos */}
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[16px] font-semibold text-[#101828]">{translateInterfaceText("Cores e Estilos")}</CardTitle>
          <CardDescription className="text-[13px] text-[#667085]">
            {translateInterfaceText("Personalize as cores dos elementos do formulário")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <CompactColorPicker
              label={translateInterfaceText("Background do Vídeo")}
              color={style.container.bg_color}
              onChange={(color) =>
                form.setValue('style.container.bg_color', color, { shouldDirty: true })
              }
            />

            <CompactColorPicker
              label={translateInterfaceText("Campo de Senha")}
              color={style.input.bg_color}
              onChange={(color) =>
                form.setValue('style.input.bg_color', color, { shouldDirty: true })
              }
            />

            <CompactColorPicker
              label={translateInterfaceText("Background Botão")}
              color={style.button.bg_color}
              onChange={(color) =>
                form.setValue('style.button.bg_color', color, { shouldDirty: true })
              }
            />

            <CompactColorPicker
              label={translateInterfaceText("Texto do Botão")}
              color={style.button.text_color}
              onChange={(color) =>
                form.setValue('style.button.text_color', color, { shouldDirty: true })
              }
            />

            <CompactColorPicker
              label={translateInterfaceText("Título")}
              color={style.text.title_color}
              onChange={(color) =>
                form.setValue('style.text.title_color', color, { shouldDirty: true })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Recursos de Interação */}
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[16px] font-semibold text-[#101828]">{translateInterfaceText("Recursos de Interação")}</CardTitle>
          <CardDescription className="text-[13px] text-[#667085]">
            {translateInterfaceText("Configure opções visuais e de comportamento do formulário")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Background (Overlay) */}
          <div className="bg-[#F7F8FE] rounded-xl p-4 space-y-3 border border-[#E2E8F0]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye
                  className={`w-5 h-5 transition-colors duration-200 ${
                    overlayEnabled ? 'text-[#2D6CF6]' : 'text-[#94A3B8]'
                  }`}
                  strokeWidth={2}
                />
                <span className="text-sm font-medium text-[#1A1A1A]">
                  {translateInterfaceText("Background")}</span>
              </div>
              <Switch
                checked={overlayEnabled}
                onCheckedChange={(checked) => {
                  form.setValue('layout.overlay_opacity', checked ? 0.7 : 0, { shouldDirty: true });
                }}
                className="data-[state=checked]:bg-[#3072FF]"
              />
            </div>
            <p className="text-[13px] text-[#6B7280] leading-relaxed pl-7">
              {translateInterfaceText("Adiciona uma camada colorida sobre o vídeo quando o formulário aparece. A cor é definida em \"Background do Vídeo\" acima")}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
